import React, { useState, useEffect, useMemo } from 'react';
import api from '../../services/api';
import { toast } from 'react-hot-toast';
import { useMontos } from '../../context/MontosContext';
import { generarPDFCierreCaja } from '../../utils/pdf/cierreCaja';
import DateInput from '../ui/DateInput';
import { getTodayISO, formatDateISO, inicioMes, finMes, fechaAR } from '../../utils/dateUtils';
import { LuCircleCheck, LuWrench, LuShoppingCart, LuFileText } from 'react-icons/lu';
import { totalServicio } from '../../utils/descuento';

function M({ valor, className = '' }) {
    const { montosVisibles } = useMontos();
    if (!montosVisibles) return <span className={className}>••••</span>;
    return <span className={className}>${typeof valor === 'number' ? Math.round(valor).toLocaleString('es-AR') : valor}</span>;
}

const hoyISO = () => getTodayISO();
const inicioMesISO = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

const PERIODOS_RAPIDOS = [
    { id: 'hoy',    label: 'Hoy'      },
    { id: 'semana', label: 'Semana'   },
    { id: 'mes',    label: 'Este mes' },
    { id: 'custom', label: 'Rango'    },
];

function resolverRango(periodo, mesInicial) {
    const hoy = hoyISO();
    if (periodo === 'hoy') return { desde: hoy, hasta: hoy };
    if (periodo === 'mes') {
        // "Este mes" arranca del mes que ya estaba elegido en Finanzas (mesInicial),
        // no siempre del mes calendario real — así el modal no contradice lo que
        // se estaba mirando en las pestañas. Si coincide con el mes real, el
        // "hasta" sigue siendo hoy (no el último día); si es un mes pasado, el
        // rango cubre el mes completo.
        if (mesInicial) {
            const [y, m] = mesInicial.split('-').map(Number);
            const ref = new Date(y, m - 1, 1);
            const mesActualReal = getTodayISO().slice(0, 7);
            return {
                desde: formatDateISO(inicioMes(ref)),
                hasta: mesInicial === mesActualReal ? hoy : formatDateISO(finMes(ref)),
            };
        }
        return { desde: inicioMesISO(), hasta: hoy };
    }
    if (periodo === 'semana') {
        const d = new Date();
        d.setDate(d.getDate() - 6);
        return { desde: formatDateISO(d), hasta: hoy };
    }
    return null;
}

export default function CierreCajaModal({ onClose, onArchivar, mesInicial }) {
    const [periodo, setPeriodo]         = useState('mes');
    const [desde, setDesde]             = useState(inicioMesISO());
    const [hasta, setHasta]             = useState(hoyISO());
    const [servicios, setServicios]     = useState([]);
    const [ventas, setVentas]           = useState([]);
    const [gastos, setGastos]           = useState([]);
    const [cargando, setCargando]       = useState(false);
    const [tecnicos, setTecnicos]       = useState([]);
    const [tecnicoFiltro, setTecnicoFiltro] = useState('');

    useEffect(() => {
        api.get('/ordenes/tecnicos').then(r => setTecnicos(r.data || [])).catch(() => {});
    }, []);

    useEffect(() => {
        const rango = resolverRango(periodo, mesInicial);
        if (rango) { setDesde(rango.desde); setHasta(rango.hasta); }
    }, [periodo, mesInicial]); // eslint-disable-line

    useEffect(() => {
        if (desde && hasta) cargar();
    }, [desde, hasta, tecnicoFiltro]); // eslint-disable-line

    const cargar = async () => {
        setCargando(true);
        try {
            // Bug real (auditoria 1-sep, critico #8): filtraba solo por el estado
            // legacy REALIZADO -- el flujo moderno de Despacho termina en COBRADO,
            // asi que un mes con caja real cobrada podia mostrar "sin movimientos".
            // Se pide cada tipo en los dos estados y se combina (misma dualidad
            // REALIZADO+COBRADO que ya usa ServicioService.calcularMesSueldo).
            const base = { desde, hasta, page: 0, size: 500, sort: 'fechaServicio,desc' };
            if (tecnicoFiltro) base.usuarioId = tecnicoFiltro;

            const [servR, servC, ventR, ventC, resGastos] = await Promise.all([
                api.get('/servicios', { params: { ...base, estado: 'REALIZADO', tipo: 'TECNICA' } }),
                api.get('/servicios', { params: { ...base, estado: 'COBRADO',   tipo: 'TECNICA' } }),
                api.get('/servicios', { params: { ...base, estado: 'REALIZADO', tipo: 'VENTA' } }),
                api.get('/servicios', { params: { ...base, estado: 'COBRADO',   tipo: 'VENTA' } }),
                api.get('/gastos/mes?mes=' + desde.substring(0, 7)).catch(() => ({ data: [] })),
            ]);
            const contenido = (r) => r.data.content || r.data || [];
            setServicios([...contenido(servR), ...contenido(servC)]);
            setVentas([...contenido(ventR), ...contenido(ventC)]);
            // Filtrar gastos por rango de fechas
            const todosGastos = resGastos.data || [];
            setGastos(todosGastos.filter(g => g.fecha >= desde && g.fecha <= hasta));
        } catch { toast.error('Error al cargar datos'); }
        finally { setCargando(false); }
    };


    // Bug real (mismo hallazgo #8): no aplicaba el descuento real de la venta/
    // servicio (descuentoPorcentaje) -- mismo fix que Balance en el backend.
    // Total con descuento por alcance: el que calcula el backend (s.totales, 9-oct-2026)
    const calcTotal = (s) => totalServicio(s);
    const calcMO        = (s) => s.items?.reduce((a, i) => a + Number(i.costoExtra || 0), 0) || 0;
    const calcRepuestos = (s) => s.items?.reduce((a, i) =>
        a + (i.repuestosUsados || []).reduce((b, r) => b + Number(r.subtotal ?? (r.precio * r.cantidad) ?? 0), 0), 0
    ) || 0;

    // 2-nov-2026: el reparto de cada trabajo lo calcula el servidor (s.reparto) con las reglas de la liquidación:
    // impuesto solo con factura, el % sumado al precio (se saca dividiendo), y vos (el dueño) no repartís.
    // Antes acá se le sacaba 30% a todo (también al efectivo) y se partía todo al medio, también lo tuyo.
    const liquidacion = (tec) => {
        const r = (s) => s.reparto || {};
        const impuestos = Math.round(tec.servicios.reduce((a, s) => a + Number(r(s).impuestosReparto || 0), 0));
        const ganancia  = Math.round(tec.servicios.reduce((a, s) => a + Number(r(s).netoTecnico || 0), 0));
        const tecnico   = Math.round(tec.servicios.reduce((a, s) => a + Number(r(s).parteTecnico || 0), 0));
        const dueno     = tec.servicios.every(s => r(s).esDueno);
        return { impuestos, ganancia, tecnico, empresa: dueno ? ganancia : ganancia - tecnico, dueno };
    };

    // Totales
    const totalServicios   = servicios.reduce((a, s) => a + calcTotal(s), 0);
    const totalVentas      = ventas.reduce((a, s) => a + calcTotal(s), 0);
    const totalIngresos    = totalServicios + totalVentas;
    const totalGastos      = gastos.reduce((a, g) => a + Number(g.monto || 0), 0);
    const moGeneral        = servicios.reduce((a, s) => a + calcMO(s), 0);
    const repuestosGeneral = servicios.reduce((a, s) => a + calcRepuestos(s), 0);
    const impuestosGeneral = Math.round(servicios.reduce((a, s) => a + Number(s.reparto?.impuestos || 0), 0));
    const netoGeneral      = totalIngresos - totalGastos - impuestosGeneral - repuestosGeneral;

    // Agrupar servicios por técnico
    const porTecnico = useMemo(() => {
        const mapa = {};
        servicios.forEach(s => {
            const key = s.usuarioNombre || 'Sin asignar';
            if (!mapa[key]) mapa[key] = { nombre: key, servicios: [], total: 0, mo: 0, repuestos: 0 };
            mapa[key].servicios.push(s);
            mapa[key].total     += calcTotal(s);
            mapa[key].mo        += calcMO(s);
            mapa[key].repuestos += calcRepuestos(s);
        });
        return Object.values(mapa).sort((a, b) => b.total - a.total);
    }, [servicios]);

    const totalItems = servicios.length + ventas.length;

    return (
        <div className="fixed inset-0 z-[2000] flex items-end md:items-center justify-center bg-black/60 backdrop-blur-sm md:pl-[calc(var(--modal-sb,0px)+1.5rem)] md:pr-6"
            onClick={onClose}>
            <div className="w-full md:max-w-lg rounded-t-3xl md:rounded-3xl max-h-[calc(var(--vh,1vh)*90)] flex flex-col bg-card shadow-2xl"
                onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className="px-5 pt-5 pb-4 border-b border-black/[0.07] dark:border-white/[0.07] flex-shrink-0">
                    <div className="w-10 h-1 rounded-full mx-auto mb-4 bg-chip md:hidden" />
                    <div className="flex items-start justify-between">
                        <div>
                            <h3 className="text-body-lg font-black uppercase tracking-tighter text-ink">
                                Cierre de Caja
                            </h3>
                            <p className="text-caption text-muted mt-0.5">Resumen del período</p>
                        </div>
                        <button onClick={onClose} className="text-muted hover:text-[#1C1917] dark:hover:text-[#F0EEE9] text-lg">✕</button>
                    </div>
                </div>

                {/* Período */}
                <div className="px-5 py-3 border-b border-black/[0.07] dark:border-white/[0.07] flex-shrink-0 space-y-2.5">
                    <div className="flex gap-2">
                        {PERIODOS_RAPIDOS.map(p => (
                            <button key={p.id} onClick={() => setPeriodo(p.id)}
                                className={`flex-1 h-8 rounded-xl font-bold text-label uppercase transition-all active:scale-95 ${periodo === p.id ? 'bg-brand-red text-white' : 'bg-chip text-secondary'}`}>
                                {p.label}
                            </button>
                        ))}
                    </div>
                    {periodo === 'custom' && (
                        <div className="flex gap-2">
                            <DateInput value={desde} onChange={setDesde}
                                className="flex-1 h-8 px-3 rounded-xl text-caption border border-black/[0.08] dark:border-white/[0.08] bg-chip text-ink outline-none" />
                            <DateInput value={hasta} onChange={setHasta}
                                className="flex-1 h-8 px-3 rounded-xl text-caption border border-black/[0.08] dark:border-white/[0.08] bg-chip text-ink outline-none" />
                        </div>
                    )}
                    {tecnicos.length > 0 && (
                        <select value={tecnicoFiltro} onChange={e => setTecnicoFiltro(e.target.value)}
                            className="w-full h-8 px-3 rounded-xl text-caption border border-black/[0.08] dark:border-white/[0.08] bg-chip text-ink outline-none">
                            <option value="">Todos los técnicos</option>
                            {tecnicos.map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}
                        </select>
                    )}
                </div>

                {/* Contenido scrolleable */}
                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
                    {cargando ? (
                        <div className="space-y-2">
                            {[1,2,3].map(i => <div key={i} className="h-16 rounded-2xl animate-pulse bg-chip" />)}
                        </div>
                    ) : totalItems === 0 && gastos.length === 0 ? (
                        <div className="text-center py-10">
                            <p className="mb-2 flex justify-center"><LuCircleCheck size={22} /></p>
                            <p className="font-bold text-muted">Sin movimientos en el período</p>
                        </div>
                    ) : (
                        <>
                            {/* ═══ RESUMEN RÁPIDO ═══ */}
                            <div className="rounded-2xl overflow-hidden border border-black/[0.07] dark:border-white/[0.07]">
                                <div className="p-4 bg-[#1C1917] dark:bg-[#0F0F0F]">
                                    <p className="text-label font-bold uppercase tracking-widest text-white/50 mb-1">Resultado neto</p>
                                    <p className={`text-[32px] font-black leading-none ${netoGeneral >= 0 ? 'text-[#4ADE80]' : 'text-[#F87171]'}`}>
                                        ${Math.round(netoGeneral).toLocaleString('es-AR')}
                                    </p>
                                </div>
                                <div className="p-3 space-y-1.5 bg-panel">
                                    <div className="flex justify-between text-body">
                                        <span className="text-secondary flex items-center gap-1"><LuWrench size={13} /> Servicios ({servicios.length})</span>
                                        <span className="font-black text-ink">${Math.round(totalServicios).toLocaleString('es-AR')}</span>
                                    </div>
                                    {totalVentas > 0 && (
                                        <div className="flex justify-between text-body">
                                            <span className="text-secondary flex items-center gap-1"><LuShoppingCart size={13} /> Ventas ({ventas.length})</span>
                                            <span className="font-black text-ink">${Math.round(totalVentas).toLocaleString('es-AR')}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between text-body">
                                        <span className="text-muted">− Impuestos (con factura)</span>
                                        <span className="font-bold text-brand-red">−${Math.round(impuestosGeneral).toLocaleString('es-AR')}</span>
                                    </div>
                                    {repuestosGeneral > 0 && (
                                        <div className="flex justify-between text-body">
                                            <span className="text-muted">− Repuestos</span>
                                            <span className="font-bold text-brand-red">−${Math.round(repuestosGeneral).toLocaleString('es-AR')}</span>
                                        </div>
                                    )}
                                    {totalGastos > 0 && (
                                        <div className="flex justify-between text-body">
                                            <span className="text-muted">− Gastos ({gastos.length})</span>
                                            <span className="font-bold text-brand-red">−${Math.round(totalGastos).toLocaleString('es-AR')}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* ═══ POR TÉCNICO ═══ */}
                            {porTecnico.length > 0 && (
                                <>
                                    <p className="text-label font-bold uppercase tracking-widest text-muted px-1">Liquidación por técnico</p>
                                    {porTecnico.map(tec => {
                                        const { impuestos, ganancia, tecnico, empresa, dueno } = liquidacion(tec);
                                        return (
                                            <div key={tec.nombre} className="rounded-2xl overflow-hidden border border-black/[0.07] dark:border-white/[0.07]">
                                                <div className="px-4 py-3 bg-[#F5F3F1] dark:bg-[#2A2A28] flex justify-between items-center">
                                                    <div>
                                                        <span className="font-black text-body text-ink">{tec.nombre}</span>
                                                        <span className="text-label text-muted ml-2">{tec.servicios.length} serv.</span>
                                                    </div>
                                                    <M valor={tec.total} className="text-body-lg font-black text-ink" />
                                                </div>
                                                <div className="px-4 py-2.5 bg-card space-y-1">
                                                    <div className="flex justify-between text-caption">
                                                        <span className="text-muted">− Impuestos (con factura)</span>
                                                        <span className="text-[#D13A28]">−${impuestos.toLocaleString('es-AR')}</span>
                                                    </div>
                                                    {tec.repuestos > 0 && (
                                                        <div className="flex justify-between text-caption">
                                                            <span className="text-muted">− Repuestos</span>
                                                            <span className="text-[#D13A28]">−${tec.repuestos.toLocaleString('es-AR')}</span>
                                                        </div>
                                                    )}
                                                    <div className="flex justify-between text-body pt-1 border-t border-black/[0.06] dark:border-white/[0.06]">
                                                        <span className="text-muted">{dueno ? 'Mano de obra neta (tuya)' : 'Mano de obra neta a repartir'}</span>
                                                        <span className="font-bold text-ink">${ganancia.toLocaleString('es-AR')}</span>
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-2 divide-x divide-black/[0.06] dark:divide-white/[0.06] border-t border-black/[0.07] dark:border-white/[0.07]">
                                                    <div className="px-4 py-2.5 bg-panel">
                                                        <p className="text-label font-bold text-muted uppercase mb-0.5">{tec.nombre.split(' ')[0]}</p>
                                                        <M valor={dueno ? 0 : tecnico} className="text-body-lg font-black text-brand-amber" />
                                                    </div>
                                                    <div className="px-4 py-2.5 bg-panel">
                                                        <p className="text-label font-bold text-muted uppercase mb-0.5">Empresa</p>
                                                        <M valor={empresa} className="text-body-lg font-black text-ink" />
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </>
                            )}

                            {/* ═══ GASTOS DEL PERÍODO ═══ */}
                            {gastos.length > 0 && (
                                <>
                                    <p className="text-label font-bold uppercase tracking-widest text-muted px-1">Gastos del período</p>
                                    <div className="rounded-2xl overflow-hidden border border-black/[0.07] dark:border-white/[0.07]">
                                        {gastos.map((g, i) => (
                                            <div key={g.id || i} className="flex justify-between px-4 py-2 text-body border-b border-black/[0.04] dark:border-white/[0.04] last:border-0">
                                                <div className="min-w-0 flex-1">
                                                    <span className="text-ink font-bold truncate block">{g.descripcion}</span>
                                                    <span className="text-caption text-muted">{fechaAR(g.fecha)} · {g.categoria}</span>
                                                </div>
                                                <span className="font-black text-brand-red shrink-0 ml-2">
                                                    −${Math.round(Number(g.monto)).toLocaleString('es-AR')}
                                                </span>
                                            </div>
                                        ))}
                                        <div className="flex justify-between px-4 py-2 bg-panel text-body">
                                            <span className="font-black text-muted uppercase">Total gastos</span>
                                            <span className="font-black text-brand-red">
                                                −${Math.round(totalGastos).toLocaleString('es-AR')}
                                            </span>
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* ═══ DETALLE SERVICIOS ═══ */}
                            {servicios.length > 0 && (
                                <>
                                    <p className="text-label font-bold uppercase tracking-widest text-muted px-1">Detalle servicios</p>
                                    <div className="rounded-2xl overflow-hidden border border-black/[0.07] dark:border-white/[0.07]">
                                        {servicios.map(s => (
                                            <div key={s.id} className="flex justify-between px-4 py-2 text-body border-b border-black/[0.04] dark:border-white/[0.04] last:border-0">
                                                <span className="text-secondary truncate mr-2">{s.clienteNombre} · {fechaAR(s.fecha)}</span>
                                                <M valor={calcTotal(s)} className="font-bold text-ink shrink-0" />
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}

                            {/* ═══ DETALLE VENTAS ═══ */}
                            {ventas.length > 0 && (
                                <>
                                    <p className="text-label font-bold uppercase tracking-widest text-muted px-1">Detalle ventas</p>
                                    <div className="rounded-2xl overflow-hidden border border-black/[0.07] dark:border-white/[0.07]">
                                        {ventas.map(v => (
                                            <div key={v.id} className="flex justify-between px-4 py-2 text-body border-b border-black/[0.04] dark:border-white/[0.04] last:border-0">
                                                <span className="text-secondary truncate mr-2">{v.clienteNombre} · {fechaAR(v.fecha)}</span>
                                                <M valor={calcTotal(v)} className="font-bold text-ink shrink-0" />
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="px-5 pb-6 pt-3 border-t border-black/[0.07] dark:border-white/[0.07] flex-shrink-0 space-y-2">
                    <div className="flex gap-3">
                        <button onClick={onClose}
                            className="flex-1 h-11 rounded-2xl font-bold text-label uppercase bg-chip text-secondary active:scale-95 transition-all">
                            Cerrar
                        </button>
                        <button
                            onClick={() => generarPDFCierreCaja({ servicios, porTecnico, totalGeneral: totalServicios, moGeneral, repuestosGeneral, desde, hasta })}
                            disabled={totalItems === 0}
                            className="flex-1 h-11 rounded-2xl font-bold text-label uppercase text-white active:scale-95 transition-all disabled:opacity-40 bg-brand-red flex items-center justify-center gap-1.5">
                            <LuFileText size={14} /> PDF
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
